import React from "react";

import { HIDDEN_TASK_TOOL_NAMES } from "@/AgentThread/AgentThreadSidebar/AgentThreadByIdView/taskSections";
import {
  AgentThreadContent,
  AgentThreadMessage,
  AgentThreadRole,
  type AgentThreadClientContextContent,
  type AgentThreadCompressionFillerContent,
  type AgentThreadDeletedContent,
  type AgentThreadErrorContent,
  type AgentThreadTextContent,
  type AgentThreadToolResultContent,
  type AgentThreadToolUseContent,
  isAgentThreadClientContextContent,
  isAgentThreadCompressionFillerContent,
  isAgentThreadDeletedContent,
  isAgentThreadErrorContent,
  isAgentThreadTextContent,
  isAgentThreadToolResultContent,
  isAgentThreadToolUseContent,
  reportUnknownAgentThreadContent,
  AgentThreadMessageStatus,
  TURN_FAILED_ERROR_CODE,
} from "@/shared/domain/agentThread/AgentThreadRecord";

import styles from "./AgentThreadMessageComponents.module.css";
import { AssistantErrorContent } from "./AssistantErrorContent";
import { AssistantTextContent } from "./AssistantTextContent";
import { AssistantToolResultContent } from "./AssistantToolResultContent";
import { UserMessage } from "./UserMessage";

// Each role branch below is an exhaustive guard chain over
// AgentThreadContent: types a role deliberately hides land in its explicit
// "known but hidden" arm, and the final `else` hands the `never`-typed
// residue to `reportUnknownAgentThreadContent`. Adding a content type to the
// union therefore fails typecheck here until every role decides to render or
// hide it, while a block from a newer server still degrades to nothing at
// runtime (plus one dev-only warning).
//
// The hidden sets are typed as predicates (not plain booleans) — without the
// `content is ...` narrowing, the trailing `else` would not reduce to `never`
// and the exhaustiveness check would be vacuous. The unions are spelled out
// member by member, NOT as `Exclude<AgentThreadContent, ...>`: an Exclude
// would silently absorb any new union member into the "hidden" set, which is
// exactly the ship-invisible failure mode this exists to prevent.

type HiddenForUser =
  | AgentThreadToolUseContent
  | AgentThreadToolResultContent
  | AgentThreadErrorContent
  | AgentThreadDeletedContent
  | AgentThreadCompressionFillerContent
  | AgentThreadClientContextContent;
const isKnownHiddenForUser = (
  content: AgentThreadContent,
): content is HiddenForUser =>
  isAgentThreadToolUseContent(content) ||
  isAgentThreadToolResultContent(content) ||
  isAgentThreadErrorContent(content) ||
  isAgentThreadDeletedContent(content) ||
  isAgentThreadCompressionFillerContent(content) ||
  isAgentThreadClientContextContent(content);

// An assistant message's tool calls render elsewhere: unanswered ones as the
// view's live call rows (see `findLiveToolCalls`), answered ones in tool groups.
type HiddenForAssistant =
  | AgentThreadToolUseContent
  | AgentThreadToolResultContent
  | AgentThreadDeletedContent
  | AgentThreadCompressionFillerContent
  | AgentThreadClientContextContent;
const isKnownHiddenForAssistant = (
  content: AgentThreadContent,
): content is HiddenForAssistant =>
  isAgentThreadToolUseContent(content) ||
  isAgentThreadToolResultContent(content) ||
  isAgentThreadDeletedContent(content) ||
  isAgentThreadCompressionFillerContent(content) ||
  isAgentThreadClientContextContent(content);

type HiddenForRoboto =
  | AgentThreadTextContent
  | AgentThreadToolUseContent
  | AgentThreadDeletedContent
  | AgentThreadCompressionFillerContent
  | AgentThreadClientContextContent;
const isKnownHiddenForRoboto = (
  content: AgentThreadContent,
): content is HiddenForRoboto =>
  // Roboto-role text chunks are internal scaffolding — ctx markers and the
  // like — and stay hidden by design.
  isAgentThreadTextContent(content) ||
  isAgentThreadToolUseContent(content) ||
  isAgentThreadDeletedContent(content) ||
  isAgentThreadCompressionFillerContent(content) ||
  isAgentThreadClientContextContent(content);

interface AgentThreadMessageComponentsProps {
  threadId: string;
  message: AgentThreadMessage;
  isLastMessageInAgentThread: boolean;
  onLinkClick?: () => void;
  onRetry?: () => void;
  /** Why the last retry from this message's failure notice did not run. */
  retryRefusal?: string;
  isCancelling?: boolean;
}

const AgentThreadMessageComponents = ({
  threadId,
  message,
  onLinkClick,
  onRetry,
  retryRefusal,
  isLastMessageInAgentThread,
  isCancelling = false,
}: AgentThreadMessageComponentsProps) => {
  const components: React.ReactNode[] = [];
  let msgIdx = 0;

  if (message.role === AgentThreadRole.USER) {
    message.content.forEach((content) => {
      if (isAgentThreadTextContent(content)) {
        const isFailed = message.status === AgentThreadMessageStatus.FAILED;

        components.push(
          <UserMessage
            key={msgIdx++}
            text={content.text}
            isFailed={isFailed}
            onRetry={isFailed ? onRetry : undefined}
          />,
        );
      } else if (isKnownHiddenForUser(content)) {
        // Role-gated allow-list: user messages render only their text.
      } else {
        reportUnknownAgentThreadContent(content);
      }
    });
  }

  if (message.role === AgentThreadRole.ASSISTANT) {
    message.content.forEach((content, idx) => {
      const isLastContentInMessage = idx === message.content.length - 1;
      const isLastContentInAgentThread =
        isLastContentInMessage && isLastMessageInAgentThread;

      if (isAgentThreadTextContent(content)) {
        components.push(
          <AssistantTextContent
            key={msgIdx++}
            text={content.text}
            isGenerating={
              isLastContentInAgentThread &&
              message.status === AgentThreadMessageStatus.GENERATING
            }
            isCancelling={isLastContentInAgentThread && isCancelling}
            onLinkClick={onLinkClick}
          />,
        );
      } else if (isAgentThreadErrorContent(content)) {
        components.push(
          <AssistantErrorContent key={msgIdx++} error={content} />,
        );
      } else if (isKnownHiddenForAssistant(content)) {
        // Role-gated allow-list: never rendered on an assistant message.
      } else {
        reportUnknownAgentThreadContent(content);
      }
    });
  }

  if (message.role === AgentThreadRole.ROBOTO) {
    // Roboto-role messages carry tool_result blocks and the occasional
    // generation error.
    message.content.forEach((content) => {
      if (isAgentThreadToolResultContent(content)) {
        // Same rule as the tool_use arm above: task-machinery results are
        // task-panel state, not transcript rows.
        if (!HIDDEN_TASK_TOOL_NAMES.has(content.tool_name)) {
          components.push(
            <AssistantToolResultContent
              key={msgIdx++}
              threadId={threadId}
              toolResult={content}
            />,
          );
        }
      } else if (isAgentThreadErrorContent(content)) {
        // A failed turn ends with this notice, and can be retried from it
        // while the notice is the thread's latest message.
        const isFailedTurnNotice =
          content.error_code === TURN_FAILED_ERROR_CODE;
        components.push(
          <AssistantErrorContent
            key={msgIdx++}
            error={content}
            onRetry={
              isFailedTurnNotice && isLastMessageInAgentThread
                ? onRetry
                : undefined
            }
            retryRefusal={isFailedTurnNotice ? retryRefusal : undefined}
          />,
        );
      } else if (isKnownHiddenForRoboto(content)) {
        // Role-gated allow-list: never rendered on a roboto message.
      } else {
        reportUnknownAgentThreadContent(content);
      }
    });
  }

  if (components.length === 0) {
    return null;
  }

  return <div className={styles.mainContainer}>{components}</div>;
};

export { AgentThreadMessageComponents };
